import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, NotificationsResponse } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendDecoprofWebhook } from "@/services/webhookService";

/**
 * Hook for setting up real-time subscriptions for notifications
 */
export const useNotificationsRealtime = (viewMode: "active" | "archived") => {
  const queryClient = useQueryClient();
  
  // Set up real-time subscription for notifications
  useEffect(() => {
    console.log(`Setting up real-time subscription for notifications table in useNotificationsRealtime (${viewMode} view)`);
    
    const notificationChannel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'notifications'
        },
        async (payload) => {
          console.log('Real-time notification change detected:', payload);
          
          // Handle different event types appropriately
          if (payload.eventType === 'INSERT') {
            const newNotification = payload.new as NotificationsResponse;
            
            // Only show toast and handle UI updates for active view when not archived and not deleted
            if (viewMode === 'active' && newNotification && !newNotification.archived && !newNotification.is_deleted) {
              // Invalidate queries - React Query will auto-refetch if component is active
              queryClient.invalidateQueries({ queryKey: ['notifications', 'active'] });
              queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
              queryClient.invalidateQueries({ queryKey: ['notifications-count', 'unread'] });
              
              // Show a toast notification for new messages
              toast.info(`Nieuwe melding: ${newNotification.type}`, {
                description: newNotification.message.substring(0, 50) + (newNotification.message.length > 50 ? '...' : '')
              });
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedNotification = payload.new as NotificationsResponse;
            const oldNotification = payload.old as NotificationsResponse;
            
            // Handle soft delete - treat it like a delete for UI purposes
            if (updatedNotification && updatedNotification.is_deleted && !oldNotification.is_deleted) {
              console.log('Notification soft-deleted:', updatedNotification.id);
              queryClient.invalidateQueries({ queryKey: ['notifications'] });
              queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
              return;
            }
            
            // Handle archived status changes
            if (updatedNotification) {
              if (updatedNotification.archived && viewMode === 'active') {
                queryClient.invalidateQueries({ queryKey: ['notifications', 'active'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'archived'] });
              } else if (!updatedNotification.archived && viewMode === 'archived') {
                queryClient.invalidateQueries({ queryKey: ['notifications', 'archived'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
                queryClient.invalidateQueries({ queryKey: ['notifications-count', 'archived'] });
              } else {
                // Optimistic update: Update specific notification without full refetch
                queryClient.setQueryData(
                  ['notifications', viewMode],
                  (old: any[] | undefined) => {
                    if (!old) return old;
                    return old.map(notif => 
                      notif.id === updatedNotification.id 
                        ? { ...notif, ...updatedNotification }
                        : notif
                    );
                  }
                );
              }
              
              // Only invalidate counts without refetching list
              queryClient.invalidateQueries({ 
                queryKey: ['notifications-count'],
                refetchType: 'none'
              });
              
              // Handle actie_decoprof status change via real-time update trigger
              if (updatedNotification.status === 'actie_decoprof' && 
                  oldNotification && oldNotification.status !== 'actie_decoprof') {
                console.log('Status changed to actie_decoprof, triggering webhook');
                // Send webhook for status change to actie_decoprof
                try {
                  const webhookResult = await sendDecoprofWebhook(
                    updatedNotification, 
                    oldNotification.status
                  );
                  
                  console.log("Decoprof webhook result:", webhookResult ? "Success" : "Failed");
                  
                  if (!webhookResult) {
                    toast.error("De status is gewijzigd, maar de webhook kon niet worden verzonden.");
                  }
                } catch (webhookError) {
                  console.error("Error sending decoprof webhook:", webhookError);
                  toast.error("De status is gewijzigd, maar de webhook kon niet worden verzonden.");
                }
              }
            }
          } else if (payload.eventType === 'DELETE') {
            // Handle deletion
            queryClient.invalidateQueries({ queryKey: ['notifications', viewMode] });
            queryClient.invalidateQueries({ queryKey: ['notifications-count'] });
          }
        }
      )
      .subscribe((status) => {
        console.log('Notification subscription status:', status);
      });

    // Add subscription for read status changes
    const readStatusChannel = supabase
      .channel('read-status-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notification_read_status'
        },
        (payload) => {
          console.log('Real-time read status change detected:', payload);
          
          // Invalidate queries - React Query will auto-refetch if component is active
          queryClient.invalidateQueries({ queryKey: ['notification-read-status'] });
          queryClient.invalidateQueries({ queryKey: ['notifications-count', 'unread'] });
        }
      )
      .subscribe((status) => {
        console.log('Read status subscription status:', status);
      });

    return () => {
      console.log('Cleaning up real-time subscriptions');
      supabase.removeChannel(notificationChannel);
      supabase.removeChannel(readStatusChannel);
    };
  }, [queryClient, viewMode]);
};
